// (c) chaehongee. Confidential and proprietary.

import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from './hooks'
import { selectActiveSessionId } from '../selectors/sessionSelectors'
import { getSessionInfo } from '../../services/session'
import type { CanvasPosition } from 'models-ts/session'
import { setSessionError, setSessionLoading, upsertSession } from '../slices/sessionSlice'
import { setPositionsFromServer } from '../slices/canvasPositionsSlice'
import { setPendingInitialSelection } from '../slices/selectionSlice'

export function useEnsureActiveSessionHydrated(): void {
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const sessionsById = useAppSelector((s) => s.session.sessionsById)
    const isLoadingById = useAppSelector((s) => s.session.isLoadingBySessionId)

    const hydratedRef = useRef<Record<string, boolean>>({})

    useEffect(() => {
        const sessionId = activeSessionId
        if (!sessionId) return
        if (isLoadingById[sessionId]) return

        const cached = sessionsById[sessionId]
        const looksLikePlaceholder = Boolean(
            cached &&
            Array.isArray((cached as any).nodes) &&
            Array.isArray((cached as any).commits) &&
            Array.isArray((cached as any).messages) &&
            (cached as any).nodes.length === 0 &&
            (cached as any).commits.length === 0 &&
            (cached as any).messages.length === 0
        )

        if (cached && !looksLikePlaceholder) return
        if (hydratedRef.current[sessionId]) return
        hydratedRef.current[sessionId] = true

        dispatch(setSessionLoading({ sessionId, isLoading: true }))
        dispatch(setSessionError({ sessionId, error: null }))

        void (async () => {
            try {
                const info = await getSessionInfo(sessionId)
                dispatch(upsertSession(info))
                try { localStorage.setItem('session_id', info.id) } catch { }

                if (info.canvas_positions) {
                    const mapped = mapApiPositions(info.canvas_positions)
                    dispatch(setPositionsFromServer({ sessionId: info.id, positions: mapped }))
                }

                // Auto-select last node when session is hydrated (matches Sessions list behavior)
                try {
                    const commits = Array.isArray((info as any).commits) ? (info as any).commits : []
                    const nodes = Array.isArray((info as any).nodes) ? (info as any).nodes : []
                    let lastNodeId: string | null = null
                    if (commits.length > 0) {
                        const lastCommit = [...commits]
                            .sort(
                                (a, b) =>
                                    new Date(a.created_at).getTime() -
                                    new Date(b.created_at).getTime()
                            )
                            .at(-1)
                        lastNodeId = lastCommit?.node_to_id ?? null
                    }
                    if (!lastNodeId && nodes.length > 0) {
                        const lastNode = [...nodes]
                            .sort(
                                (a, b) =>
                                    new Date(a.created_at).getTime() -
                                    new Date(b.created_at).getTime()
                            )
                            .at(-1)
                        lastNodeId = lastNode?.id ?? null
                    }
                    if (lastNodeId) {
                        dispatch(setPendingInitialSelection({ sessionId, nodeId: lastNodeId }))
                    }
                } catch { }
            } catch (e: any) {
                dispatch(setSessionError({ sessionId, error: e?.message ?? 'Failed to load session' }))
            } finally {
                dispatch(setSessionLoading({ sessionId, isLoading: false }))
            }
        })()
    }, [activeSessionId, sessionsById, isLoadingById, dispatch])
}

function mapApiPositions(positions: Record<string, CanvasPosition>) {
    const mapped: Record<
        string,
        { x: number; y: number; w: number; h: number; manuallySet: boolean; timestamp: number }
    > = {}
    for (const [id, pos] of Object.entries(positions)) {
        mapped[id] = {
            x: pos.x,
            y: pos.y,
            w: pos.w,
            h: pos.h,
            manuallySet: pos.manually_set,
            timestamp: pos.timestamp,
        }
    }
    return mapped
}
